import React, { useEffect, useMemo } from "react";
import TaskHeader from "components/task/header/TaskHeader";
import { Box, Button, Stack, Typography } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import ContentTemplateV2 from "components/task/content/ContentTemplateV2";
import { useSearchParams } from "react-router-dom";
import faqData from "../../data/faqData.json";
import { CoachingFaq } from "../../api/coachingFaqApi";
import CustomTypography from "../../components/common/CustomTypography";
import { theme } from "../../styles/theme";
import { getConditionalFaqContent } from "../../utils/faqUtils";
import BackButton from "../../components/common/BackButton";
import FullPageContainer from "../container/FullPageContainer";
import useSafeAreaColor from "../../hooks/useSafeAreaColor";
import { linkifyText } from "../../utils/linkifyUtils";
import { useChannelTalkContext } from "../../contexts/ChannelTalkContext";
import useGoBack from "../../hooks/useGoBack";
import useBottomSafePadding from "../../hooks/useBottomSafePadding";

export default function SupportFaqDetailPage() {
  const user = useRecoilValue(userAtom);
  const { openChat } = useChannelTalkContext();
  const [searchParams] = useSearchParams();
  const faqId =
    searchParams.get("fid") !== null
      ? parseInt(searchParams.get("fid")!!)
      : null;

  const isFullPage = window.location.pathname === "/support/faq/detail";
  const backgroundColor = theme.vars.palette.Background.Level1;
  useSafeAreaColor(backgroundColor);
  const goBack = useGoBack();
  // isFullPage 모드에서 안쪽 Stack 이 height: 100vh 로 있어 FullPageContainer 의
  // pb=env() 가 설정되더라도 해당 Stack 이 부모 제약을 무시하고 패딩을
  // 무력화함 (팍앤트 content 영역이 (100vh - env) 보다 크게 되어 오버플로). 그
  // 결과 iOS 에서 마지막 라인이 home indicator 에 바짝 점점하는 버그.
  // 안쪽 scroll Stack 자체에 pb 를 주어 보장.
  const innerScrollPb = useBottomSafePadding("16px");

  // Find the FAQ item that matches the faqId
  const faqItem = useMemo(() => {
    if (faqId === null) return null;

    // Find the original FAQ item
    const originalFaqItem =
      (faqData as CoachingFaq[]).find((item) => item.faqId === faqId) || null;

    // Apply conditional content based on user properties
    return originalFaqItem
      ? getConditionalFaqContent(originalFaqItem, user)
      : null;
  }, [faqId, user]);

  const child = faqItem ? (
    <Stack
      sx={{
        ...(isFullPage && { px: `20px` }),
        ...(!isFullPage && { pb: innerScrollPb }),
      }}
    >
      <CustomTypography
        variant={"H3"}
        sx={{
          color: (theme) => theme.vars.palette.Content.Strong,
          fontWeight: 700,
        }}
      >
        {faqItem.question}
      </CustomTypography>
      <Box sx={{ mt: 2 }}>
        <CustomTypography
          variant={"Body"}
          sx={{
            color: (theme) => theme.vars.palette.Content.Medium,
          }}
        >
          {linkifyText(faqItem.answer)}
          {"\n\n도움이 되었나요? 더 궁금한 것이 있다면 "}
          <Box
            component="a"
            onClick={(e) => {
              e.preventDefault();
              openChat();
            }}
            sx={{
              textDecoration: "underline",
              textUnderlineOffset: "2px",
              color: (theme) => theme.vars.palette.Content.Medium,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            여기
          </Box>
          {"로 직접 문의해주세요. 영업일 기준 1일 내로 답변해 드릴게요."}
        </CustomTypography>
      </Box>
    </Stack>
  ) : (
    <>
      <Stack
        alignItems={"center"}
        justifyContent={"center"}
        spacing={2}
        sx={{ height: "100%" }}
      >
        <Typography
          variant="Body"
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 500,
            textAlign: "center",
          }}
        >
          {"검색 결과가 없어요.\n" + "도움이 필요하시면 언제든 문의주세요."}
        </Typography>
        <Button
          variant={"Soft"}
          size={"md"}
          sx={{
            width: "200px",
            height: "46px",
            fontWeight: 500,
            fontSize: "14px",
          }}
          onClick={() => {
            openChat();
          }}
        >
          {"직접 문의하기"}
        </Button>
      </Stack>
    </>
  );

  return isFullPage ? (
    <FullPageContainer backgroundColor={backgroundColor}>
      <Stack
        direction={"column"}
        justifyContent={"flex-start"}
        sx={{
          maxWidth: "660px",
          width: "100%",
          mx: "auto",
          height: "100vh",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            position: "sticky",
            top: 0,
            zIndex: 100,
            height: `60px`, // 헤더 높이 설정
            px: 1,
          }}
        >
          <BackButton onBack={goBack} />
        </Stack>
        <Stack
          sx={{
            width: "100%",
            flex: 1,
            overflow: "auto",
            pt: 0,
            pb: innerScrollPb,
            position: "relative",
          }}
        >
          {child}
        </Stack>
      </Stack>
    </FullPageContainer>
  ) : (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <TaskHeader
          key={`support-faq_${user?.userId}_${faqId}`}
          taskKey={`support-faq-${user?.userId}_${faqId}`}
          emptyTitle={true}
        />
      </Box>

      <ContentTemplateV2 disableBottomSafeArea>{child}</ContentTemplateV2>
    </>
  );
}
